import { Component, ElementRef, OnDestroy, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faDiagramProject, faFileContract, faHouse, faMagnifyingGlass, faUser, faUsers
} from '@fortawesome/pro-light-svg-icons';
import {
  GLOBAL_SEARCH_MIN_LENGTH,
  GlobalSearchHit,
  GlobalSearchKind,
  GlobalSearchService
} from '../../../services/global-search.service';
import { recordLink } from '../../utils/current-record';
import { CommandPaletteData, PaletteSwitch, isGraphShortcut } from './palette-switch';
import { PaletteSwitcherComponent, PaletteTab } from './palette-switcher.component';

/** Délai après la dernière frappe avant d'interroger la base. */
const DEBOUNCE_MS = 180;

/**
 * Titre et icône de chaque famille — les icônes de la barre latérale, pour
 * que la palette se lise comme le menu.
 */
const KIND_META: Record<GlobalSearchKind, { title: string; icon: IconDefinition }> = {
  lease: { title: 'Baux', icon: faFileContract },
  owner: { title: 'Propriétaires', icon: faUser },
  tenant: { title: 'Locataires', icon: faUsers },
  property: { title: 'Biens', icon: faHouse },
};

interface HitGroup {
  kind: GlobalSearchKind;
  title: string;
  icon: IconDefinition;
  /** Les lignes, avec leur rang dans la liste à plat que parcourent les flèches. */
  rows: { hit: GlobalSearchHit; index: number }[];
}

/**
 * La palette de commande : une barre de recherche sur les baux, propriétaires,
 * locataires et biens, ouverte par Cmd+K (Ctrl+K hors Mac) — voir
 * `CommandPaletteService`. Aucun bouton ne l'ouvre, par choix (2026-09-24).
 *
 * Tout le parc, pour tout le monde : c'est un outil pour retrouver une fiche,
 * le filtre gestionnaire ne s'y applique pas.
 *
 * Clavier : ↑/↓ parcourent la liste à plat, Entrée ouvre la fiche, Cmd/Ctrl+
 * Entrée l'ouvre dans un nouvel onglet, Cmd/Ctrl+Shift+K passe au graphe du
 * propriétaire centré sur la ligne active, Échap ferme (MatDialog). Le focus
 * reste dans le champ ; la ligne active est annoncée par `aria-activedescendant`.
 *
 * Rouverte depuis le graphe, elle reprend la saisie précédente, sélectionnée :
 * on fait l'aller-retour liste → graphe → liste sans retaper, et la moindre
 * frappe la remplace.
 */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [FontAwesomeModule, RouterLink, PaletteSwitcherComponent],
  templateUrl: './command-palette.component.html',
})
export class CommandPaletteComponent implements OnInit, OnDestroy {
  private readonly searchService = inject(GlobalSearchService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialogRef<CommandPaletteComponent>);
  private readonly data = inject<CommandPaletteData | null>(MAT_DIALOG_DATA, { optional: true });

  private readonly listbox = viewChild<ElementRef<HTMLElement>>('listbox');
  private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');

  readonly faMagnifyingGlass = faMagnifyingGlass;
  readonly faDiagramProject = faDiagramProject;
  readonly minLength = GLOBAL_SEARCH_MIN_LENGTH;
  readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  readonly query = signal('');
  readonly hits = signal<GlobalSearchHit[]>([]);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly activeIndex = signal(0);
  /** La saisie à laquelle `hits` répond — « aucun résultat » ne s'affiche que pour elle. */
  readonly answeredQuery = signal<string | null>(null);

  readonly tooShort = computed(() => this.query().trim().length < GLOBAL_SEARCH_MIN_LENGTH);

  readonly groups = computed<HitGroup[]>(() => {
    const groups: HitGroup[] = [];
    this.hits().forEach((hit, index) => {
      let group = groups.find(g => g.kind === hit.kind);
      if (!group) {
        group = { kind: hit.kind, ...KIND_META[hit.kind], rows: [] };
        groups.push(group);
      }
      group.rows.push({ hit, index });
    });
    return groups;
  });

  readonly activeId = computed(() =>
    this.hits().length ? this.optionId(this.activeIndex()) : null
  );

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Numéro de la dernière requête partie. Les appels Amplify sont des promesses,
   * on ne peut pas les annuler : une réponse lente à « du » qui arriverait après
   * celle à « dupont » écraserait la bonne liste. On l'ignore donc à l'arrivée.
   */
  private requestSeq = 0;

  ngOnInit(): void {
    const previous = this.data?.query ?? '';
    if (previous.trim() === '') return;
    this.onInput(previous);
    // Après le focus que pose MatDialog : sélectionner, pour que la première
    // frappe remplace l'ancienne saisie au lieu de s'y ajouter.
    setTimeout(() => this.input()?.nativeElement.select());
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  onInput(value: string): void {
    this.query.set(value);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (value.trim().length < GLOBAL_SEARCH_MIN_LENGTH) {
      this.requestSeq++;
      this.hits.set([]);
      this.loading.set(false);
      this.error.set(false);
      this.answeredQuery.set(null);
      return;
    }

    this.loading.set(true);
    this.debounceTimer = setTimeout(() => void this.run(value), DEBOUNCE_MS);
  }

  private async run(value: string): Promise<void> {
    const seq = ++this.requestSeq;
    try {
      const hits = await this.searchService.search(value);
      if (seq !== this.requestSeq) return;
      this.hits.set(hits);
      this.error.set(false);
    } catch {
      if (seq !== this.requestSeq) return;
      this.hits.set([]);
      this.error.set(true);
    }
    this.activeIndex.set(0);
    this.answeredQuery.set(value);
    this.loading.set(false);
  }

  onKeydown(event: KeyboardEvent): void {
    if (isGraphShortcut(event, this.isMac)) {
      // Arrêté ici : sans quoi le raccourci global ouvrirait le graphe de
      // l'ÉCRAN, et non de la ligne choisie.
      event.preventDefault();
      event.stopPropagation();
      this.showGraph(this.hits()[this.activeIndex()] ?? null);
      return;
    }
    const count = this.hits().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (count) this.setActive((this.activeIndex() + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (count) this.setActive((this.activeIndex() - 1 + count) % count);
        break;
      case 'Enter': {
        event.preventDefault();
        const hit = this.hits()[this.activeIndex()];
        if (hit) this.open(hit, event.metaKey || event.ctrlKey);
        break;
      }
    }
  }

  setActive(index: number): void {
    this.activeIndex.set(index);
    // Après le rendu : la ligne active doit rester visible quand la liste défile.
    queueMicrotask(() => {
      this.listbox()?.nativeElement
        .querySelector(`#${this.optionId(index)}`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  }

  /**
   * Clic sur une ligne. Le lien porte un vrai `href` : Cmd/Ctrl+clic et clic
   * molette ouvrent donc un onglet sans rien faire ici, et la palette reste
   * ouverte pour la ligne suivante. Un clic simple navigue et ferme.
   */
  onRowClick(event: MouseEvent): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    this.dialogRef.close();
  }

  /**
   * Passer au graphe du propriétaire, centré sur `hit` — sans ligne, sur la
   * fiche de l'écran. Le service ouvre le graphe à la fermeture.
   */
  showGraph(hit: GlobalSearchHit | null, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    const result: PaletteSwitch = { switchTo: 'graph', anchor: hit ? { kind: hit.kind, id: hit.id } : null };
    this.dialogRef.close(result);
  }

  /** Clic sur « Graphe » au pied de la palette : ce que fait Cmd+Shift+K. */
  onSwitch(tab: PaletteTab): void {
    if (tab === 'graph') this.showGraph(this.hits()[this.activeIndex()] ?? null);
  }

  /** L'infobulle de « Graphe » : sur quelle fiche il s'ouvrira. */
  readonly graphHint = computed(() => {
    const hit = this.hits()[this.activeIndex()];
    return hit ? `Voir « ${hit.label} » dans le graphe du propriétaire` : 'Graphe du propriétaire de la fiche ouverte';
  });

  open(hit: GlobalSearchHit, newTab: boolean): void {
    const commands = this.link(hit);
    if (newTab) {
      window.open(this.router.serializeUrl(this.router.createUrlTree(commands)), '_blank');
      return;
    }
    this.dialogRef.close();
    void this.router.navigate(commands);
  }

  /** La fiche de chaque famille — les mêmes routes que les listes. */
  link(hit: GlobalSearchHit): string[] {
    return recordLink(hit.kind, hit.id);
  }

  optionId(index: number): string {
    return `command-palette-option-${index}`;
  }
}
